import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ArrowRight, Brain, CheckCircle2, Lightbulb, RefreshCw, Send, ShieldAlert, Target, TrendingUp } from 'lucide-react';
import { GlassCard, GlassButton, GlassInput, FadeIn } from '../../components/ui';
import { api } from '../../hooks/useApi';

const priorityClasses = {
  critica: 'border-red-400/30 bg-red-500/10 text-red-100',
  alta: 'border-amber-400/30 bg-amber-500/10 text-amber-100',
  media: 'border-blue-400/30 bg-blue-500/10 text-blue-100',
  baixa: 'border-white/10 bg-white/[0.04] text-white/65',
};

const moduleLabels = {
  financeiro: 'Financeiro',
  construtora: 'Construtora',
  rh: 'RH',
  frota: 'Frota',
  estoque: 'Estoque',
  imobiliaria: 'Imobiliária',
  predictive: 'Predictive',
};

const formatDateTime = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('pt-BR');
};

export const Predictive = () => {
  const [radar, setRadar] = useState(null);
  const [question, setQuestion] = useState('');
  const [copilot, setCopilot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState('');

  const loadRadar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/predictive/radar');
      setRadar(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao carregar radar preditivo.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRadar();
  }, [loadRadar]);

  const signals = radar?.signals || [];
  const topSignal = signals[0] || null;

  const askCopilot = async () => {
    if (!question.trim()) return;
    setAsking(true);
    setError('');
    try {
      const response = await api.post('/predictive/copilot', {
        question,
        page_context: window.location.pathname.replace('/erp', '') || '/predictive',
      });
      setCopilot(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao consultar copiloto.');
    } finally {
      setAsking(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Predictive AI</h1>
          <p className="text-white/50">Radar operacional e copiloto contextual do ERP</p>
        </div>
        <GlassButton icon={loading ? RefreshCw : Brain} onClick={loadRadar} disabled={loading}>
          {loading ? 'Analisando...' : 'Atualizar radar'}
        </GlassButton>
      </div>

      {error && (
        <GlassCard className="border-red-400/30 bg-red-500/10 p-4" hover={false}>
          <p className="text-sm text-red-100">{error}</p>
        </GlassCard>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        <GlassCard className="p-5" hover={false}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">Score geral</p>
              <p className="mt-2 text-2xl font-semibold text-white/90">{radar?.score_geral || 0}</p>
            </div>
            <Target className="h-6 w-6 text-blue-300" />
          </div>
        </GlassCard>
        <GlassCard className="p-5" hover={false}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">Sinais</p>
              <p className="mt-2 text-2xl font-semibold text-white/90">{radar?.resumo?.sinais_total || 0}</p>
            </div>
            <Brain className="h-6 w-6 text-cyan-300" />
          </div>
        </GlassCard>
        <GlassCard className="p-5" hover={false}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">Alta prioridade</p>
              <p className="mt-2 text-2xl font-semibold text-white/90">{radar?.resumo?.criticos_altos || 0}</p>
            </div>
            <ShieldAlert className="h-6 w-6 text-amber-300" />
          </div>
        </GlassCard>
        <GlassCard className="p-5" hover={false}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">Oportunidades</p>
              <p className="mt-2 text-2xl font-semibold text-white/90">{radar?.resumo?.oportunidades || 0}</p>
            </div>
            <Lightbulb className="h-6 w-6 text-emerald-300" />
          </div>
        </GlassCard>
      </div>

      {topSignal && (
        <GlassCard className={`p-5 ${priorityClasses[topSignal.prioridade] || priorityClasses.baixa}`} hover={false}>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-4xl">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5" />
                <p className="text-sm uppercase tracking-wide">Sinal dominante</p>
              </div>
              <h2 className="mt-3 text-xl font-semibold text-white/95">{topSignal.titulo}</h2>
              <p className="mt-2 text-sm text-white/70">{topSignal.descricao}</p>
              <p className="mt-3 text-sm text-white/85">{topSignal.recomendacao}</p>
            </div>
            <div className="rounded-lg border border-white/10 bg-black/20 px-4 py-3 text-right">
              <p className="text-xs text-white/45">Score</p>
              <p className="text-2xl font-semibold text-white/90">{topSignal.score}</p>
            </div>
          </div>
        </GlassCard>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <GlassCard className="xl:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 p-6">
            <div>
              <h3 className="text-lg font-semibold text-white/90">Sinais por prioridade</h3>
              <p className="text-sm text-white/50">{formatDateTime(radar?.generated_at)}</p>
            </div>
            <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-sm text-white/60">
              {radar?.maturidade_preditiva || 'operacional'}
            </span>
          </div>
          <div className="divide-y divide-white/10">
            {signals.map((signal) => (
              <div key={`${signal.module}-${signal.titulo}`} className="grid grid-cols-1 gap-4 p-5 lg:grid-cols-12">
                <div className="lg:col-span-2">
                  <span className={`inline-flex rounded-full border px-3 py-1 text-xs ${priorityClasses[signal.prioridade] || priorityClasses.baixa}`}>
                    {signal.prioridade}
                  </span>
                  <p className="mt-2 text-xs text-white/40">{moduleLabels[signal.module] || signal.module}</p>
                </div>
                <div className="lg:col-span-8">
                  <h4 className="font-semibold text-white/90">{signal.titulo}</h4>
                  <p className="mt-1 text-sm text-white/55">{signal.descricao}</p>
                  <p className="mt-2 text-sm text-white/75">{signal.recomendacao}</p>
                </div>
                <div className="flex items-center justify-between lg:col-span-2 lg:justify-end lg:gap-4">
                  <span className="text-lg font-semibold text-white/90">{signal.score}</span>
                  {signal.path && (
                    <a className="text-blue-300 hover:text-blue-200" href={`/erp${signal.path}`}>
                      <ArrowRight className="h-5 w-5" />
                    </a>
                  )}
                </div>
              </div>
            ))}
            {!signals.length && <p className="p-6 text-sm text-white/50">Sem sinais preditivos no momento.</p>}
          </div>
        </GlassCard>

        <div className="space-y-6">
          <GlassCard>
            <div className="border-b border-white/10 p-6">
              <h3 className="text-lg font-semibold text-white/90">Módulos monitorados</h3>
            </div>
            <div className="divide-y divide-white/10">
              {(radar?.modules || []).map((module) => (
                <div key={module.module} className="p-4">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-white/85">{moduleLabels[module.module] || module.module}</p>
                    <span className="text-sm text-white/55">{module.score}</span>
                  </div>
                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10">
                    <div className="h-full rounded-full bg-blue-400" style={{ width: `${Math.min(module.score, 100)}%` }} />
                  </div>
                  <p className="mt-2 text-xs text-white/40">{module.count} sinais, {module.criticos} altos</p>
                </div>
              ))}
            </div>
          </GlassCard>

          <GlassCard className="p-5">
            <div className="flex items-center gap-3">
              <Brain className="h-5 w-5 text-cyan-300" />
              <h3 className="font-semibold text-white/90">Copiloto ERP</h3>
            </div>
            <div className="mt-4 space-y-3">
              <GlassInput
                label="Pergunta"
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') askCopilot();
                }}
                placeholder="Ex.: por que preciso cobrar documentos da empreiteira?"
              />
              <GlassButton icon={asking ? RefreshCw : Send} onClick={askCopilot} disabled={asking || !question.trim()} className="w-full">
                {asking ? 'Consultando...' : 'Perguntar'}
              </GlassButton>
            </div>
          </GlassCard>
        </div>
      </div>

      {copilot && (
        <FadeIn>
          <GlassCard className="p-6">
            <div className="flex items-start gap-4">
              <div className="rounded-lg border border-cyan-400/20 bg-cyan-500/10 p-3">
                <CheckCircle2 className="h-5 w-5 text-cyan-200" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-white/90">Resposta do copiloto</h3>
                <p className="mt-2 text-sm leading-6 text-white/70">{copilot.answer}</p>
                <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
                  {(copilot.suggested_actions || []).map((action) => (
                    <a
                      key={`${action.label}-${action.path}`}
                      href={action.path ? `/erp${action.path}` : '/erp/predictive'}
                      className="rounded-lg border border-white/10 bg-white/[0.03] p-4 transition-colors hover:bg-white/[0.06]"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-medium text-white/85">{action.label}</p>
                        <TrendingUp className="h-4 w-4 text-white/35" />
                      </div>
                      <p className="mt-2 text-xs text-white/45">{action.priority}</p>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </GlassCard>
        </FadeIn>
      )}
    </div>
  );
};

export default Predictive;
